import { useEffect, useRef } from 'react'

/**
 * How far below the fold the sentinel starts asking. A page of products is
 * roughly two screens, so 400px is "the shopper is on the last row" — early
 * enough that the next page usually lands before they reach the end, late
 * enough that a shopper who stops scrolling does not pull the whole catalog.
 */
const ROOT_MARGIN = '400px'

/**
 * Calls `onReach` while the returned ref's element is in view.
 *
 * An `IntersectionObserver` rather than a scroll listener: the page scrolls the
 * window, the grid is inside a flex column, and a scroll handler would have to
 * measure both on every frame. The observer fires only when the sentinel
 * actually crosses the margin, and it is the reason nothing here runs while the
 * shopper is reading.
 *
 * `enabled` is what stops it: the caller passes `hasMore && !loading`, so an
 * observer that is still attached cannot ask for a page while one is in flight,
 * and cannot ask at all once the server says there is no next page. The
 * callback lives in a ref so a new closure — `loadMore` is rebuilt whenever the
 * view changes — does not tear the observer down and set it up again.
 */
export const useInfiniteScroll = (enabled: boolean, onReach: () => void) => {
  const sentinel = useRef<HTMLDivElement | null>(null)
  const latest = useRef(onReach)

  useEffect(() => {
    latest.current = onReach
  }, [onReach])

  useEffect(() => {
    const element = sentinel.current
    if (!enabled || element === null) return

    // jsdom has no IntersectionObserver; a page test asserts on the button
    // fallback instead, and this keeps the component rendering there.
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) latest.current()
      },
      { rootMargin: ROOT_MARGIN },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [enabled])

  return sentinel
}
